import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  Rocket,
  ArrowLeft,
  PartyPopper,
  Users,
  UserCheck,
  AlertCircle,
  Target,
} from "lucide-react";
import { useAgent } from "../../context/AgentContext";
import { Input } from "../../components/Input";
import Button from "../../components/Button";
import Card from "../../components/Card";

export default function StepGoLive() {
  const navigate = useNavigate();
  const { goPrev } = useOutletContext();
  const { currentAgent, launchAgent, isLoading, error } = useAgent();

  // Local state for launch settings
  const [respondTo, setRespondTo] = useState(currentAgent?.respond_to || "all");
  const [whitelist, setWhitelist] = useState(currentAgent?.whitelist || []);
  const [whitelistInput, setWhitelistInput] = useState("");
  const [showConfetti, setShowConfetti] = useState(false);
  const [localError, setLocalError] = useState(null);

  const handleAddNumber = () => {
    if (whitelistInput.trim()) {
      setWhitelist((prev) => [...prev, whitelistInput.trim()]);
      setWhitelistInput("");
    }
  };

  const handleRemoveNumber = (number) => {
    setWhitelist((prev) => prev.filter((n) => n !== number));
  };

  const handleGoLive = async () => {
    setLocalError(null);

    try {
      // Call the backend to launch the agent
      await launchAgent({
        respond_to: respondTo,
        whitelist: respondTo === "whitelist" ? whitelist : [],
      });

      // Show confetti
      setShowConfetti(true);

      // Wait for celebration
      await new Promise((resolve) => setTimeout(resolve, 2500));

      // Navigate to the agent dashboard
      navigate(`/agent/${currentAgent.id}`);
    } catch (err) {
      setLocalError(err.message || "Failed to launch agent");
    }
  };

  const displayError = localError || error;

  return (
    <div className="p-8 max-w-2xl mx-auto animate-fade-in relative">
      {/* Confetti Overlay */}
      {showConfetti && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/95 backdrop-blur-sm">
          <div className="text-center animate-slide-up relative z-10">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-primary to-accent mb-6 animate-bounce-subtle">
              <PartyPopper className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-text-primary mb-2">
              You're Live!
            </h2>
            <p className="text-text-secondary">
              Your AI agent is ready to chat
            </p>
          </div>
          {/* Confetti particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {Array.from({ length: 50 }).map((_, i) => (
              <div
                key={i}
                className="absolute w-3 h-3 rounded-full animate-bounce"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `-5%`,
                  backgroundColor: [
                    "#4F46E5",
                    "#A78BFA",
                    "#22C55E",
                    "#F59E0B",
                    "#EC4899",
                  ][i % 5],
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${2 + Math.random() * 3}s`,
                  transform: `rotate(${Math.random() * 360}deg)`,
                }}
              />
            ))}
          </div>
        </div>
      )}

      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent">
            <Rocket className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Go Live!</h1>
            <p className="text-text-secondary">Final settings before launch</p>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {displayError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-700 text-sm font-medium">Error</p>
            <p className="text-red-600 text-sm">{displayError}</p>
          </div>
        </div>
      )}

      {/* Response Mode */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-text-primary">
          Who should your AI respond to?
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => setRespondTo("all")}
            className={`
              card p-5 text-left transition-all
              ${
                respondTo === "all"
                  ? "border-primary ring-2 ring-primary/20"
                  : "hover:border-primary/30"
              }
            `}
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className={`
                p-2 rounded-lg
                ${
                  respondTo === "all"
                    ? "bg-primary text-white"
                    : "bg-primary/10 text-primary"
                }
              `}
              >
                <Users className="w-5 h-5" />
              </div>
              <span className="font-semibold text-text-primary">
                All Contacts
              </span>
            </div>
            <p className="text-sm text-text-muted">
              Respond to messages from anyone who contacts your WhatsApp
            </p>
          </button>

          <button
            onClick={() => setRespondTo("whitelist")}
            className={`
              card p-5 text-left transition-all
              ${
                respondTo === "whitelist"
                  ? "border-primary ring-2 ring-primary/20"
                  : "hover:border-primary/30"
              }
            `}
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className={`
                p-2 rounded-lg
                ${
                  respondTo === "whitelist"
                    ? "bg-primary text-white"
                    : "bg-primary/10 text-primary"
                }
              `}
              >
                <UserCheck className="w-5 h-5" />
              </div>
              <span className="font-semibold text-text-primary">
                Specific Numbers
              </span>
            </div>
            <p className="text-sm text-text-muted">
              Only respond to specific phone numbers you approve
            </p>
          </button>
        </div>
      </div>

      {/* Whitelist Input */}
      {respondTo === "whitelist" && (
        <div className="mt-6 animate-slide-up">
          <Card className="p-6">
            <div className="flex gap-2">
              <Input
                placeholder="254712345678"
                value={whitelistInput}
                onChange={(e) => setWhitelistInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddNumber()}
                className="flex-1"
              />
              <Button
                variant="secondary"
                onClick={handleAddNumber}
                disabled={!whitelistInput.trim()}
              >
                Add
              </Button>
            </div>

            {whitelist.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-sm text-text-muted">Approved numbers:</p>
                <div className="flex flex-wrap gap-2">
                  {whitelist.map((number, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg text-sm"
                    >
                      {number}
                      <button
                        onClick={() => handleRemoveNumber(number)}
                        className="text-text-muted hover:text-red-500"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Summary */}
      <div className="mt-8 p-6 bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl border border-primary/10">
        <h3 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-primary" />
          Ready to launch
        </h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-text-muted">Agent Name</p>
            <p className="font-medium text-text-primary">
              {currentAgent?.name || "Unnamed"}
            </p>
          </div>
          <div>
            <p className="text-text-muted">Business Type</p>
            <p className="font-medium text-text-primary">
              {currentAgent?.business_type || "Not set"}
            </p>
          </div>
          <div>
            <p className="text-text-muted">Language</p>
            <p className="font-medium text-text-primary">
              {currentAgent?.language || "English"}
            </p>
          </div>
          <div>
            <p className="text-text-muted">Response Mode</p>
            <p className="font-medium text-text-primary">
              {respondTo === "all" ? "All contacts" : "Whitelist only"}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8">
        <Button variant="ghost" icon={ArrowLeft} onClick={goPrev}>
          Back
        </Button>
        <Button
          icon={Rocket}
          onClick={handleGoLive}
          loading={isLoading}
          className="!px-8"
        >
          {isLoading ? "Launching..." : "Go Live!"}
        </Button>
      </div>
    </div>
  );
}
